'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Clock, UserX, Bus, ChevronRight, X, BellRing, TrendingDown, Timer, ShieldAlert } from 'lucide-react';
import Link from 'next/link';
import { detectPerformanceWarnings, type PerformanceWarning } from '@/lib/supabase/services/performanceWarningService';

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info';
  category: 'duty' | 'crew' | 'bus' | 'grade_drop' | 'ontime_rate' | 'violation_pattern';
  title: string;
  detail: string;
  time: string;
  actionLabel?: string;
  actionHref?: string;
  isNew?: boolean;
}

const alertConfig = {
  critical: { border: 'border-negative/30', iconBg: 'bg-negative/12 text-negative', label: 'bg-negative/12 text-negative', labelText: 'CRITICAL', pulse: true },
  warning: { border: 'border-warning/30', iconBg: 'bg-warning/12 text-warning', label: 'bg-warning/12 text-warning', labelText: 'WARNING', pulse: false },
  info: { border: 'border-primary/20', iconBg: 'bg-primary/12 text-primary', label: 'bg-primary/12 text-primary', labelText: 'INFO', pulse: false },
};

const categoryIcon = {
  duty: Clock,
  crew: UserX,
  bus: Bus,
  grade_drop: TrendingDown,
  ontime_rate: Timer,
  violation_pattern: ShieldAlert,
};

function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function warningToAlert(w: PerformanceWarning): Alert {
  return {
    id: w.id,
    type: w.type,
    category: w.category,
    title: w.title,
    detail: w.detail,
    time: formatTime(w.detectedAt),
    actionLabel: 'View Details',
    actionHref: w.actionHref,
    isNew: true,
  };
}

export default function UrgentAlerts() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [blinkNew, setBlinkNew] = useState(true);
  const [loading, setLoading] = useState(true);

  const fetchWarnings = useCallback(async () => {
    try {
      const warnings = await detectPerformanceWarnings();
      const newAlerts = warnings.map(warningToAlert);
      setAlerts(newAlerts);
    } catch (err) {
      console.error('UrgentAlerts: failed to fetch warnings', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWarnings();
    // Re-check every 5 minutes
    const interval = setInterval(fetchWarnings, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWarnings]);

  useEffect(() => {
    const t = setTimeout(() => setBlinkNew(false), 5000);
    return () => clearTimeout(t);
  }, []);

  const visibleAlerts = alerts.filter((a) => !dismissed.has(a.id));
  const criticalCount = visibleAlerts.filter((a) => a.type === 'critical').length;

  if (loading) {
    return (
      <div className="glass-card rounded-2xl p-5 mb-6 animate-pulse">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-muted" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 bg-muted rounded w-1/3" />
            <div className="h-2.5 bg-muted rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (visibleAlerts.length === 0) {
    return (
      <div className="glass-card rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-positive/12 flex items-center justify-center">
            <BellRing size={16} className="text-positive" />
          </div>
          <div>
            <p className="text-sm font-700 text-foreground">All Clear</p>
            <p className="text-xs text-muted-foreground">No performance warnings detected</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            {criticalCount > 0 && <span className={`w-2 h-2 rounded-full bg-negative ${blinkNew ? 'animate-ping' : 'animate-pulse'}`} />}
            <AlertTriangle size={15} className={criticalCount > 0 ? 'text-negative' : 'text-warning'} />
          </div>
          <h3 className="text-sm font-700 text-foreground">Performance Warnings</h3>
          <span className={`text-[10px] font-700 uppercase tracking-wider px-2 py-0.5 rounded-full ${criticalCount > 0 ? 'bg-negative/12 text-negative' : 'bg-warning/12 text-warning'}`}>
            {visibleAlerts.length} active
          </span>
        </div>
        <span className="text-xs text-muted-foreground">Tap × to dismiss</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {visibleAlerts.map((alert) => {
          const cfg = alertConfig[alert.type];
          const CatIcon = categoryIcon[alert.category] ?? UserX;
          return (
            <div key={alert.id} className={`glass-card rounded-2xl border p-4 ${cfg.border} ${alert.isNew && blinkNew ? 'ring-1 ring-negative/30' : ''} relative transition-all duration-300`}>
              <button onClick={() => setDismissed((prev) => new Set([...prev, alert.id]))} className="absolute top-3 right-3 w-5 h-5 flex items-center justify-center rounded-full hover:bg-black/10 dark:hover:bg-white/10 transition-colors duration-150" aria-label="Dismiss">
                <X size={11} className="text-muted-foreground" />
              </button>
              <div className="flex items-start gap-3 pr-5">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${cfg.iconBg}`}>
                  <CatIcon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <span className={`text-[9px] font-700 uppercase tracking-widest px-1.5 py-0.5 rounded-md ${cfg.label}`}>{cfg.labelText}</span>
                    {alert.isNew && <span className="text-[9px] font-700 uppercase tracking-widest px-1.5 py-0.5 rounded-md bg-foreground/8 text-foreground">NEW</span>}
                  </div>
                  <p className="text-sm font-700 text-foreground leading-snug">{alert.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{alert.detail}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[10px] text-muted-foreground tabular-nums">{alert.time}</span>
                    {alert.actionLabel && alert.actionHref && (
                      <Link href={alert.actionHref} className={`flex items-center gap-0.5 text-xs font-700 transition-colors duration-150 ${alert.type === 'critical' ? 'text-negative hover:text-negative/80' : alert.type === 'warning' ? 'text-warning hover:text-warning/80' : 'text-primary hover:text-primary/80'}`}>
                        {alert.actionLabel} <ChevronRight size={11} />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
